import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { SyntheticEvent } from 'react';

import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import { Icon } from 'chatie-design-system/icon';

import { media } from '@styles/css';

import EFinderImage, {
  EFinderImageSizes,
} from '@components/common/EFinderImage';

import IcPlay from '@public/icons/ic_play.svg';

import { IProfileByInfoPlacardFormList } from '@base/outlookModel';
import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { formatNumberMillion } from '@utils/chat';
import { useWebView } from '@utils/webview';

const containerCSS = () => css`
  display: flex;
  flex-direction: column;
  padding: 10px ${Spacing.medium}px 0;
  ${media.tablet} {
    padding: 10px ${Spacing.xlarge}px 0;
  }
  ${media.desktop} {
    padding: 10px ${Spacing.xxlarge}px 0;
  }

  .header {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    margin-bottom: ${Spacing.xhsmall}px;
  }
  .listContainer {
    display: flex;
    flex-direction: column;
    gap: ${Spacing.xsmall}px;
  }
`;
const cardContainerCSS = (theme: Theme) => css`
  display: flex;
  flex: 1;
  padding: ${Spacing.small}px;
  border-radius: ${Radius.small}px;

  box-shadow: 0px 0px 10px 2px rgba(0, 0, 0, 0.05);

  .imageContainer {
    width: 36px;
    height: 36px;
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
    position: relative;
    overflow: hidden;
  }

  .image {
    background-color: ${theme.var.gray200};
    width: 36px;
    height: 36px;
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
    overflow: hidden;
  }
  .infoContainer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    width: 100%;
    overflow: hidden;
  }
  .name {
    ${Typography.body12};
    margin-bottom: ${Spacing.xhsmall}px;
  }
  .playCount {
    display: flex;
    gap: ${Spacing.xxxsmall}px;
    ${Typography.body11};
    color: ${theme.var.gray400};
  }
`;

interface ProfileByInfoPlacardFormListProps {
  data: IProfileByInfoPlacardFormList;
  onClickSituationById?: (situationId: number) => void;
}

const ProfileByInfoPlacardFormList = ({
  data,
  onClickSituationById,
}: ProfileByInfoPlacardFormListProps) => {
  const { t } = useTranslation();
  const { title, profiles } = data;
  const { web } = useWebView();
  const { platform, isCheming } = useGlobalContext();

  const theme = useTheme();
  const router = useRouter();

  const handleNavigateCharacter = (e: SyntheticEvent, id: number) => {
    e.stopPropagation();
    if (platform === Platform.web) {
      router.push(`/play/characters/${id}`);
    } else {
      web.openPage(`/play/characters/${id}`);
    }
  };

  return (
    <div css={containerCSS}>
      <div className="header">
        <h5 suppressHydrationWarning>{t(title)}</h5>
      </div>
      <div className="listContainer">
        {profiles.map((item, idx) => (
          <div
            css={cardContainerCSS}
            key={`info_list_${idx}`}
            onClick={(e) => {
              if (isCheming) {
                return handleNavigateCharacter(e, item.id);
              }
              onClickSituationById &&
                item?.payload?.dynamic_object?.situation_id &&
                onClickSituationById(
                  item?.payload?.dynamic_object?.situation_id
                );
            }}
          >
            <div className="imageContainer">
              {item.image && item.image.key ? (
                <EFinderImage
                  image={item.image}
                  h={EFinderImageSizes.avatar}
                  alt={'profile_img'}
                />
              ) : (
                <div className="image" />
              )}
            </div>

            <div className="infoContainer">
              <h6 className="name">{item.title}</h6>
              {!isCheming && (
                <div className="playCount">
                  <Icon
                    icon={IcPlay}
                    size={Sizes.xsmall}
                    color={theme.var.gray400}
                  />
                  <p>
                    {formatNumberMillion(
                      item.payload.dynamic_object?.message_count || 0
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileByInfoPlacardFormList;
